import React from 'react'
import { InfoOutlined } from "@mui/icons-material";
import { Badge, Box, CircularProgress, IconButton, Typography } from "@mui/material";
import { Link } from "react-router-dom";

import AppTable from "components/Shared/AppTable";
import AppTableDateTime from "components/Shared/AppTableDateTime";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";

import { useAuth, useTablePagination } from "hooks";
import { useCodTransferDetailContext } from "contexts/PaymentContexts/CodTransferContext";
import { useLazyListShipmentsByCodTransferIdQuery } from "store/services/PaymentsApi/codTransferApi";


const CodTransferShipmentList = () => {

  const { CodTransferDetail } = useCodTransferDetailContext();

  const { isVendor, isCustomer } = useAuth();

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const columns = [
    {
      name: "ID",
      selector: (row) => row?.shipmentId,
      cell: (row) => row?.shipmentId,
      width: "150px",
    },
    {
      name: "Sender",
      selector: (row) =>
        row?.customerUser || row?.vendorShopName || "N/A",
      cell: (row) => (
        <Box sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {row?.customerUser ||
            <div>
              <span className="text-sm">{row?.vendorShopName || "N/A"}</span>
              <br />
              <span className="text-sm text-gray-500 dark:text-gray-300">
                {row?.vendorOwnerName	 || "N/A"}
              </span>
            </div>
            || "N/A"}
        </Box>
      ),
      omit: isVendor,
    },
    {
      name: "Receiver",
      selector: (row) => row?.receiver?.customerUser,
      cell: (row) => (
        <div>
          <span className="text-sm">{row?.receiver?.customerUser || "N/A"}</span>
          <br />
          <span className="text-sm text-gray-500 dark:text-gray-300">
            {row?.receiver?.phone || "N/A"}
          </span>
        </div>
      ),
    },
    {
      name: "From",
      selector: (row) => row?.sourceBranch || "Loading...",
      cell: (row) => (
        <span>{row?.sourceBranch || <CircularProgress size={12} />}</span>
      ),
    },
    {
      name: "To",
      selector: (row) => row?.destinationBranch || "Loading...",
      cell: (row) => (
        <span>{row?.destinationBranch || <CircularProgress size={12} />}</span>
      ),
    },
    {
      name: "Pickup Time",
      selector: (row) => <AppTableDateTime date={row?.shipmentPickupTime} />,
    },
    {
      name: "Status",
      selector: (row) => row.status,
      center: true,
      cell: (row) => {
        const statusColors = {
          Pending: "bg-yellow-700 text-white",
          Collected: "bg-gray-500 text-white",
          Loaded: "bg-gray-500 text-white",
          Delivered: "bg-gray-500 text-white",
        };
        return (
          <Badge className={`px-1 py-1 rounded-md ${statusColors[row.status] || "bg-gray-500"}`}>
            {row.status}
          </Badge>
        );
      },
    },
    {
      name: "Actions",
      cell: (row) => {
        const basePath = isVendor
          ? "/app/vendor/shipment"
          : isCustomer
            ? "/app/customer/shipment"
            : "/app/crm/shipment";
        return (
          <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
            <Link to={`${basePath}/${row?._id}/detail`}>
              <IconButton size="small" aria-label="View Shipment" color="primary">
                <InfoOutlined />
              </IconButton>
            </Link>
          </Box>
        );
      },
      ignoreRowClick: true,
      right: true,
    },
  ];

  const [fetchShipments, { data: shipmentRes, isLoading, isFetching }] =
    useLazyListShipmentsByCodTransferIdQuery();

  React.useEffect(() => {
    if (!CodTransferDetail?._id) return;
    fetchShipments({
      codId: CodTransferDetail?._id,
      page: currentPage,
      limit: perPage,
    });
  }, [currentPage, fetchShipments, perPage, CodTransferDetail?._id]);

  return (
    <>
      <BreadcrumbLayout.Paper>
        {shipmentRes ? (
          <AppTable
            columns={columns}
            data={shipmentRes?.data || []}
            progressPending={isLoading || isFetching}
            paginationServer
            paginationPerPage={perPage}
            paginationTotalRows={shipmentRes?.totalItems || 0}
            paginationDefaultPage={currentPage}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
          />
        ) : (
          <Box display="flex" alignItems="center" justifyContent="center" padding={2}>
            <CircularProgress size={24} />
            <Typography variant="body2" marginLeft={1}>Loading Shipments...</Typography>
          </Box>
        )}
      </BreadcrumbLayout.Paper>
    </>
  );
};


export default CodTransferShipmentList